import {
  ExperiencePerformanceTypes,
  ExperienceTypes,
  UFOExperience,
  untilAll,
} from '@atlassian/ufo';

import { getAnalyticsClient } from '@/common/ufo/index';

// NOTE: GlobalPageLoadExperience finishes when all 'PRODUCT' type PageSegmentLoad
// experiences finish.

/**
 * Experience that measures the first loading time of the LargeSlos component.
 * Succeeds when the first page of SLOs is loaded.
 */
export const largeSlosExperience = new UFOExperience('large-slos', {
  category: 'PRODUCT',
  performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
  type: ExperienceTypes.Load,
});

/**
 * Experience that measures the loading time of the CapabilityList component
 * from the time of initial render or pagination change to the time where the
 * rows and summary lozenges have been rendered.
 */
export const reportCapabilityListExperience = new UFOExperience('report-capability-list', {
  category: 'COMPONENT',
  performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
  type: ExperienceTypes.Load,
});

/**
 * Similar to CapabilityListExperience but for the product list.
 */
export const reportProductListExperience = new UFOExperience('report-product-list', {
  category: 'COMPONENT',
  performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
  type: ExperienceTypes.Load,
});

/**
 * Similar to CapabilityListExperience but for the team list.
 */
export const reportTeamListExperience = new UFOExperience('report-team-list', {
  category: 'COMPONENT',
  performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
  type: ExperienceTypes.Load,
});

/**
 * Overarching report view experience. Because it has the category 'PRODUCT' it is responsible
 * for completing the global page load experience.
 */
export const reportViewExperience = new UFOExperience('report-view', {
  category: 'PRODUCT',
  performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
  type: ExperienceTypes.Load,
  until: untilAll([
    { experience: reportCapabilityListExperience },
    { experience: reportProductListExperience },
    { experience: reportTeamListExperience },
  ]),
});

// Feel free to add more values as usage changes
export enum ViewInSignalFxButtonClickedEventPageSource {
  SloPage = 'sloPage',
  SloCard = 'sloCard',
}

export const callbackViewInSignalFxButtonClickedEvent = (
  pageSource: ViewInSignalFxButtonClickedEventPageSource,
  publicId: string
) => {
  const onViewErrorBudgetInSignalFxButtonClicked = () => {
    const client = getAnalyticsClient();
    client?.sendUIEvent({
      actionSubject: 'button',
      action: 'clicked',
      actionSubjectId: 'viewInSignalFx',
      objectType: 'slo',
      objectId: publicId,
      source: pageSource,
    });
  };

  const onViewBurnRatesInSignalFxButtonClicked = () => {
    const client = getAnalyticsClient();
    client?.sendUIEvent({
      actionSubject: 'button',
      action: 'clicked',
      actionSubjectId: 'viewBurnRatesInSignalFx',
      objectType: 'slo',
      objectId: publicId,
      source: pageSource,
    });
  };

  return { onViewErrorBudgetInSignalFxButtonClicked, onViewBurnRatesInSignalFxButtonClicked };
};

/**
 * Experience that measures the first loading time of the Search component.
 * Succeeds when the search page data are loaded.
 */
export const searchViewExperience = new UFOExperience('search-view', {
  category: 'PRODUCT',
  performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
  type: ExperienceTypes.Load,
});

/**
 * Experience that measures the first loading time of the homepage component.
 * Succeeds when the homepage data are loaded.
 */
export const homepageViewExperience = new UFOExperience('homepage-view', {
  category: 'PRODUCT',
  performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
  type: ExperienceTypes.Load,
});

/**
 * Experience that measures the first loading time of the Team component.
 * Succeeds when the team page data are loaded.
 */
export const teamViewExperience = new UFOExperience('team-view', {
  category: 'PRODUCT',
  performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
  type: ExperienceTypes.Load,
});

export const callbackErrorBudgetGraphSelectGraphDateEvent = () => {
  const onErrorBudgetGraphSelectGraphDate = () => {
    const client = getAnalyticsClient();
    client?.sendUIEvent({
      actionSubject: 'button',
      action: 'clicked',
      actionSubjectId: 'selectGraphDate',
      source: 'errorBudgetGraph',
    });
  };

  return { onErrorBudgetGraphSelectGraphDate };
};

/**
 * Maps the type to the corresponding experience.
 */
// eslint-disable-next-line consistent-return
export const typeToExperience = (type: 'product' | 'team' | 'capability'): UFOExperience => {
  switch (type) {
    case 'product':
      return reportProductListExperience;
    case 'team':
      return reportTeamListExperience;
    case 'capability':
      return reportCapabilityListExperience;
  }
};
